import { useEffect, useState } from 'react';
import { phoneField } from '../phone';
import { ApiError, get, post } from '../api';
import { Combobox, type ComboOption } from './Combobox';

type Role = 'customer' | 'client' | 'contractor' | 'referrer';

interface Found {
  id: number;
  display_name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  state?: string;
}

const NOUN: Record<Role, string> = {
  customer: 'customer',
  client: 'client',
  contractor: 'contractor',
  referrer: 'OT / rehab company',
};

/** Clients and referrers are usually organisations; customers and contractors are people. */
const ORGANISATION: Record<Role, boolean> = {
  customer: false,
  client: true,
  contractor: false,
  referrer: true,
};

const PLURAL: Record<Role, string> = {
  customer: 'customers',
  client: 'clients',
  contractor: 'contractors',
  referrer: 'OT / rehab companies',
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

/** Everything a new contact needs beyond its name, per role. */
const PROFILE: Record<Role, Record<string, unknown>> = {
  customer: {},
  referrer: {},
  client: { client: { tax_code: 'gst' } },
  contractor: { contractor: { gst_applicable: false } },
};

/**
 * Pick a person or organisation by role: type to search, or add a new one
 * without leaving the form. `0` means nobody. Shows who is picked (and, for a
 * contractor, how to reach them) so a wrong pick is easy to spot.
 */
export function ContactPicker(props: {
  role: Role;
  /** Names the field, e.g. "Client / Insurer". */
  label: string;
  value: number;
  onChange(id: number): void;
  error?: string;
  /** Ids to leave out, e.g. team members already chosen. */
  exclude?: number[];
  /** Show a card for the picked person. */
  card?: boolean;
  /** Contractors doing any of these types of work only; none means any. */
  contractorTypes?: string[];
  placeholder?: string;
}) {
  const [picked, setPicked] = useState<Found | null>(null);
  const [text, setText] = useState('');
  const [matches, setMatches] = useState<Found[]>([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  // Joined, so the search below reruns only when the types themselves change.
  const types = (props.contractorTypes ?? []).join(',');

  // Whoever is picked (a saved assignment loads with an id): fetch their name once.
  useEffect(() => {
    if (props.value <= 0) {
      setPicked(null);
      setText('');
      return;
    }
    if (picked?.id === props.value) return;
    let live = true;
    get<Found>(`/api/contacts/${props.value}`)
      .then((c) => {
        if (live && c && c.display_name) {
          setPicked(c);
          setText(c.display_name);
        }
      })
      .catch(() => {
        // The name is a convenience; the id is what counts.
      });
    return () => {
      live = false;
    };
  }, [props.value]);

  // Search as they type, after a short pause so each keystroke is not a request.
  useEffect(() => {
    const q = picked && text === picked.display_name ? '' : text.trim();
    let live = true;
    setBusy(true);
    const t = setTimeout(() => {
      get<{ items: Found[] }>(
        `/api/contacts?role=${props.role}&limit=8${q ? `&q=${encodeURIComponent(q)}` : ''}` +
          (types ? `&contractor_types=${encodeURIComponent(types)}` : ''),
      )
        .then((r) => live && setMatches(Array.isArray(r.items) ? r.items : []))
        .catch(() => live && setMatches([]))
        .finally(() => live && setBusy(false));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [text, props.role, picked, types]);

  const options: ComboOption[] = matches
    .filter((m) => !(props.exclude ?? []).includes(m.id))
    .map((m) => ({
      id: m.id,
      label: m.display_name,
      detail: m.email || m.mobile || m.phone || '',
    }));

  const pick = (c: Found) => {
    setPicked(c);
    setText(c.display_name);
    props.onChange(c.id);
  };

  return (
    <Combobox
      label={props.label}
      text={text}
      onText={(t) => {
        setText(t);
        // Typing over a pick un-picks it, so the box never says one thing and the form another.
        if (props.value > 0) props.onChange(0);
      }}
      options={options}
      onPick={(o) => pick(matches.find((m) => m.id === o.id)!)}
      placeholder={props.placeholder ?? `Search ${PLURAL[props.role]}…`}
      footer={{ label: `+ Add new ${NOUN[props.role]}`, onChoose: () => setCreating(true) }}
      error={props.error}
      hasValue={props.value > 0}
      onClear={() => props.onChange(0)}
      busy={busy}
    >
      {props.card && picked && props.value > 0 && (
        <div className="who-card">
          <span className="avatar" aria-hidden="true">
            {initials(picked.display_name)}
          </span>
          <span>
            <strong>{picked.display_name}</strong>
            <span className="muted">{picked.mobile || picked.phone || picked.email || ''}</span>
          </span>
          {picked.state && (
            <span className={picked.state === 'active' ? 'pill ok' : 'pill'}>
              {picked.state === 'active' ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>
      )}
      {creating && (
        <NewContact
          role={props.role}
          contractorTypes={props.contractorTypes}
          start={text}
          onCancel={() => setCreating(false)}
          onCreated={(c) => {
            setCreating(false);
            pick(c);
          }}
        />
      )}
    </Combobox>
  );
}

function NewContact(props: {
  role: Role;
  contractorTypes?: string[];
  start: string;
  onCancel(): void;
  onCreated(c: Found): void;
}) {
  const org = ORGANISATION[props.role];
  const [first, setFirst] = useState(org ? '' : (props.start.split(' ')[0] ?? ''));
  const [last, setLast] = useState(org ? '' : props.start.split(' ').slice(1).join(' '));
  const [name, setName] = useState(org ? props.start : '');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    setProblems([]);
    // A contractor added while types of work are chosen does the first of them.
    const type = props.contractorTypes?.[0];
    const profile =
      props.role === 'contractor' && type
        ? { contractor: { gst_applicable: false, contractor_type: type } }
        : PROFILE[props.role];
    try {
      const body = org
        ? { kind: 'organisation', name, email, phone, roles: [props.role], ...profile }
        : {
            kind: 'person',
            first_name: first,
            last_name: last,
            email,
            mobile: phone,
            roles: [props.role],
            ...profile,
          };
      const r = await post<{ id: number }>('/api/contacts', body);
      props.onCreated({
        id: r.id,
        display_name: org ? name : `${first} ${last}`.trim(),
        email,
        mobile: phone,
        state: 'active',
      });
    } catch (e) {
      setProblems(
        e instanceof ApiError
          ? e.problems.length
            ? e.problems
            : [e.message]
          : ['Could not reach the server'],
      );
      setBusy(false);
    }
  }

  return (
    <div className="new-contact" role="group" aria-label={`New ${NOUN[props.role]}`}>
      <strong>New {NOUN[props.role]}</strong>
      {org ? (
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
      ) : (
        <div className="row">
          <label>
            First name
            <input value={first} onChange={(e) => setFirst(e.target.value)} />
          </label>
          <label>
            Last name
            <input value={last} onChange={(e) => setLast(e.target.value)} />
          </label>
        </div>
      )}
      <label>
        Email
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Phone
        <input value={phone} onChange={(e) => setPhone(phoneField(e.target.value))} />
      </label>
      {problems.length > 0 && (
        <ul role="alert" className="error">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div className="row">
        <button type="button" disabled={busy} onClick={() => void create()}>
          Create &amp; use
        </button>
        <button type="button" className="quiet" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
